import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import BulkFileDropzone, {
  BULK_DEMO_UPLOAD_HINT
} from '../../../Home/components/agent/BulkFileDropzone';
import BulkUploadReview from '../../../Home/components/profiler/BulkUploadReview';
import { useStore } from '../../../../store/store';

/**
 * TD-2344 Seam 5 — the "Adjust search" body for a bulk company search. Reuses the exact same
 * pipeline as the Home bulk search: dropzone -> (parent hook uploads + polls) -> BulkUploadReview
 * (select) -> finalize -> onFinalized() navigates to the new searchId's results.
 *
 * Upload/poll state is owned by the parent's single useBulkCompanySearch instance and handed in via
 * `bulkAdjust`; this component only renders the current step. It does NOT duplicate the review.
 *
 * TD-2382: mirrors the Home bulk form — while the match worker runs only the upload field flips
 * to a loading animation (`loading`), and submit/poll errors surface through the standard
 * top-of-screen system message (raised by useBulkCompanySearch), never as inline text.
 *
 * TD-2537: also mirrors the Home form's demo gate. This is the second render site of the
 * dropzone, reachable via "Adjust search" -> category "Bulk", so it needs the same treatment
 * or a demo user still lands in the upload-then-403 dead-end the gate exists to remove.
 */
const BulkAdjustPanel = observer(({ bulkAdjust }) => {
  const { profilerAccessStore } = useStore();
  const { bulk, file, onFileChange, onFinalized } = bulkAdjust;
  const bulkResults = bulk.status === 'completed' ? bulk.results : null;
  const bulkLoading = bulk.status === 'uploading' || bulk.status === 'pending';
  const isDemo = profilerAccessStore.isProfilerDemo;

  if (bulkResults) {
    return (
      <BulkUploadReview
        results={bulkResults}
        searchId={bulk.searchId}
        onBack={() => {
          bulk.reset();
          onFileChange(null);
        }}
        onFinalized={onFinalized}
      />
    );
  }

  return (
    <>
      <BulkFileDropzone
        file={file}
        onFileChange={onFileChange}
        loading={bulkLoading}
        progress={bulk.progress}
        disabled={isDemo}
        disabledHint={BULK_DEMO_UPLOAD_HINT}
      />
      <div className="fields-row">
        <div />
        <div />
        <div className="search-btn-wrap">
          <button
            type="button"
            className="btn btn-primary disable-fade-primary apply-criteria-button"
            disabled={!file || bulkLoading || isDemo}
            onClick={() => {
              // Guard the handler as well as the button — this calls bulk.start
              // directly rather than going through the Home form's submitBulk.
              if (isDemo) return;
              bulk.start(file);
            }}
          >
            Search
          </button>
        </div>
      </div>
    </>
  );
});

BulkAdjustPanel.propTypes = {
  bulkAdjust: PropTypes.shape({
    bulk: PropTypes.shape({
      status: PropTypes.string.isRequired,
      results: PropTypes.object,
      searchId: PropTypes.string,
      error: PropTypes.string,
      progress: PropTypes.shape({
        processed: PropTypes.number,
        total: PropTypes.number
      }),
      start: PropTypes.func.isRequired,
      reset: PropTypes.func.isRequired
    }).isRequired,
    file: PropTypes.object,
    onFileChange: PropTypes.func.isRequired,
    onFinalized: PropTypes.func.isRequired
  }).isRequired
};

export default BulkAdjustPanel;
